// chat/Chat.tsx — the chat panel UI (cloned from Oriora Expense's src/chat/Chat.tsx). Purely
// ADDITIVE: it drives the SAME capabilities the app already has (log / edit / delete / search /
// summarize / reflect) by typing. All styling is namespaced under .omc-* (Oriora Mood Chat) so it
// can't collide with any existing app CSS.
//
// HITL: a delete proposed by the router is held in `pending` and only runs on an explicit Confirm
// tap (a chat message can't destroy data on its own) — mirrors Expense's discipline.

import { useState, useRef, useEffect } from "react";
import { askBrain, type ChatTurn } from "./brain";
import { cloudBackupStatus } from "../brain";
import { executeAction, type ChatAction, type FilterRow } from "./actions";
import { emojiForScore } from "../store";
import { MicButton, SpeakButton } from "./voice";
import CallOverlay from "../live/CallOverlay";

const STORE_KEY = "oriora.mood.chat.v1"; // device-only; lost on clear/reinstall

const fmtDay = (iso: string) =>
  new Date(iso.slice(0, 10) + "T00:00:00Z").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" });

const GREETING: ChatTurn = {
  role: "assistant",
  content:
    'Tell me what to do — I\'ll log it, find it, summarize, reflect, or answer questions about your own patterns, no tapping.\n\ne.g. "log good today with work" · "show my rough days this month" · "which day am I usually lowest?" · "reflect on my week".',
};

function loadChat(): ChatTurn[] {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (raw) { const parsed = JSON.parse(raw); if (Array.isArray(parsed) && parsed.length) return parsed; }
  } catch { /* ignore — start fresh */ }
  return [GREETING];
}
function saveChat(turns: ChatTurn[]) {
  try { localStorage.setItem(STORE_KEY, JSON.stringify(turns.slice(-100))); } catch { /* ignore */ }
}

// Destructive actions are NEVER auto-run from a chat turn — they wait for a Confirm tap.
const destructive = (k: ChatAction["kind"]) =>
  k === "delete_entry" || k === "delete_tag" || k === "backup_restore" || k === "import_file" ||
  k === "library_remove" || k === "library_merge";

const warnFor = (action: ChatAction): string =>
  action.kind === "library_merge"
    ? `Merge ${action.label ?? "these items"} into one? The originals are folded into the new item — tap Confirm, or Cancel.`
    : action.kind === "library_remove"
      ? `Remove ${action.label ?? "that item"} from your library? This can't be undone — tap Confirm, or Cancel.`
      : action.kind === "import_file"
    ? "Importing will REPLACE everything in the app with the file you pick. Tap Confirm to choose a file, or Cancel."
    : action.kind === "backup_restore"
      ? `Restore will REPLACE everything in the app with your ${action.provider === "gdrive" ? "Google Drive" : action.provider === "github" ? "GitHub" : "cloud"} backup. Tap Confirm to proceed, or Cancel.`
      : action.kind === "delete_tag"
        ? `Remove the tag "${action.tag}" from every entry that has it? Tap Confirm, or Cancel to keep it.`
        : `Delete ${(action.kind === "delete_entry" && action.label) || "this entry"}? This can't be undone — tap Confirm, or Cancel to keep it.`;

const tailNote = (rest: ChatAction[]): string =>
  rest.length ? `\n\n(${rest.length} more step${rest.length > 1 ? "s" : ""} from this request will run after you confirm.)` : "";

// A held destructive action PLUS the rest of its queue. Confirm runs the action then RESUMES the
// queue (re-holding at any further destructive); Cancel drops the tail and says so. Without the
// tail, "delete yesterday and back up" silently never backs up — the exact G17 half-work class.
type PendingHold = { action: ChatAction; rest: ChatAction[] };

export default function Chat({ onChanged }: { onChanged?: () => void }) {
  const [turns, setTurns] = useState<ChatTurn[]>(loadChat);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [onCall, setOnCall] = useState(false);
  // A delete is held here until the user taps Confirm — the router proposing it is NOT enough.
  const [pending, setPending] = useState<PendingHold | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [turns, busy, pending]);
  useEffect(() => { saveChat(turns); }, [turns]);

  const transcriptOf = (t: ChatTurn[]) => t.map((x) => `${x.role}: ${x.content}`).join("\n");

  async function send() {
    const text = input.trim();
    if (!text || busy) return;
    const next: ChatTurn[] = [...turns, { role: "user", content: text }];
    setTurns(next);
    setInput("");
    setBusy(true);
    try {
      // Streaming: show the assistant bubble immediately and grow it with each chunk; every
      // settle path below overwrites it via setTurns([...next, …]) with the authoritative reply.
      let streamed = "";
      const onChunk = (t: string) => { streamed += t; setTurns([...next, { role: "assistant", content: streamed }]); };
      setTurns([...next, { role: "assistant", content: "" }]);
      const { reply, actions } = await askBrain(next, onChunk);
      // Run every non-destructive action in order — "log Mon good and Tue rough" must do BOTH (G17).
      const r = await runQueue(actions, transcriptOf(next));
      if (r.hold) {
        setPending(r.hold);
        const warn = warnFor(r.hold.action) + tailNote(r.hold.rest);
        setTurns([...next, { role: "assistant", content: [reply, ...r.lines, warn].filter(Boolean).join("\n\n"), rows: r.rows }]);
      } else {
        const body = [reply, ...r.lines].filter(Boolean).join("\n\n");
        setTurns([...next, { role: "assistant", content: body || "Done.", rows: r.rows }]);
      }
      if (r.changed) onChanged?.();
    } catch (e) {
      setTurns([...next, { role: "assistant", content: `Something went wrong: ${e instanceof Error ? e.message : String(e)}` }]);
    } finally {
      setBusy(false);
    }
  }

  // Run actions in order until the first destructive one, which is returned as a hold with its tail.
  async function runQueue(queue: ChatAction[], transcript: string) {
    const lines: string[] = [];
    let rows: FilterRow[] | undefined;
    let changed = false;
    for (let i = 0; i < queue.length; i++) {
      const action = queue[i];
      if (destructive(action.kind)) {
        // RESTORE STORY: the confirm must NAME the source. When the customer didn't name one,
        // resolve the priority source (what the panel's status line shows) before the hold.
        let held = action;
        if (held.kind === "backup_restore" && !held.provider) {
          try {
            const s = await cloudBackupStatus();
            if (s.writes_to) held = { ...held, provider: s.writes_to };
          } catch { /* hold falls back to "cloud" wording */ }
        }
        return { lines, rows, changed, hold: { action: held, rest: queue.slice(i + 1) } as PendingHold };
      }
      const result = await executeAction(action, { transcript });
      if (result.message) lines.push(result.message);
      if (result.rows) rows = result.rows;
      if (result.changed) changed = true;
    }
    return { lines, rows, changed, hold: null };
  }

  async function confirmPending() {
    if (!pending || busy) return;
    const { action, rest } = pending;
    setPending(null);
    setBusy(true);
    try {
      const result = await executeAction(action, { transcript: transcriptOf(turns) });
      const lines = result.message ? [result.message] : [];
      let rows = result.rows;
      let changed = !!result.changed;
      // Resume the rest of the request — a further destructive action re-holds, same rule.
      const r = await runQueue(rest, transcriptOf(turns));
      lines.push(...r.lines);
      if (r.rows) rows = r.rows;
      changed = changed || r.changed;
      if (r.hold) {
        setPending(r.hold);
        lines.push(warnFor(r.hold.action) + tailNote(r.hold.rest));
      }
      setTurns((t) => [...t, { role: "assistant", content: lines.join("\n\n") || "Done.", rows }]);
      if (changed) onChanged?.();
    } catch (e) {
      setTurns((t) => [...t, { role: "assistant", content: `Something went wrong: ${e instanceof Error ? e.message : String(e)}` }]);
    } finally {
      setBusy(false);
    }
  }

  function newChat() {
    if (busy) return;
    setPending(null);
    setTurns([GREETING]);
    try { localStorage.removeItem(STORE_KEY); } catch { /* ignore */ }
  }

  function cancelPending() {
    const skipped = pending?.rest.length ?? 0;
    setPending(null);
    setTurns((t) => [...t, {
      role: "assistant",
      content: skipped
        ? `Okay — kept it. I also skipped the ${skipped} remaining step${skipped > 1 ? "s" : ""} of that request; ask again if you still want ${skipped > 1 ? "them" : "it"}.`
        : "Okay — kept it. Tell me what else to do.",
    }]);
  }

  return (
    <div className="omc-chat">
      <div className="omc-scroll">
        {turns.length > 1 && (
          <div style={{ textAlign: "center", marginBottom: "0.4rem" }}>
            <button type="button" className="link" style={{ fontSize: "var(--fs-xxs)", opacity: 0.75 }} onClick={newChat} disabled={busy}>
              ↺ Start a new chat
            </button>
          </div>
        )}
        {turns.map((t, i) => (
          <div key={i} className={`omc-msgrow ${t.role === "user" ? "me" : ""}`}>
            <div className={`omc-bubble ${t.role === "user" ? "me" : "bot"}`}>
              {t.content}
              {t.rows && t.rows.length > 0 && <FilterRows rows={t.rows} />}
              {t.role !== "user" && t.content.trim() ? (<div style={{ marginTop: 2, textAlign: "right" }}><SpeakButton text={t.content} /></div>) : null}
            </div>
          </div>
        ))}
        {busy && <div className="omc-msgrow"><div className="omc-bubble bot thinking">thinking</div></div>}
        <div ref={endRef} />
      </div>
      {pending ? (
        <div className="omc-composer omc-confirm">
          <button className="btn btn-primary" style={{ background: "var(--rose)", borderColor: "var(--rose)" }} onClick={confirmPending} disabled={busy}>{pending && String(pending.action.kind).startsWith("delete") ? "Confirm delete" : "Confirm"}</button>
          <button className="btn" onClick={cancelPending} disabled={busy}>Cancel</button>
        </div>
      ) : (
        <div className="omc-composer">
          <textarea
            value={input}
            placeholder="Tell me what to do…"
            rows={2}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          />
          <MicButton value={input} onChange={setInput} disabled={busy} />
            {/* Every surface. iOS runs the native voice plugin; macOS and web use the browser's
                own audio (getUserMedia + its echo canceller) — the stack every desktop voice
                assistant uses. The gate here used to be iOS-only because no other path existed. */}
            {<button
              type="button"
              className="btn btn-pill"
              title="Talk to it live"
              aria-label="Start a live call"
              style={{ flexShrink: 0, fontSize: 16 }}
              onClick={() => setOnCall(true)}
            >📞</button>}
            {/* Mounted right beside the button on purpose: CallOverlay is a fixed-position
                sheet, so it renders correctly anywhere in the tree, and this needs no
                structural analysis of each app's JSX — a button whose overlay never got
                mounted is the half-wired failure this roll exists to avoid. */}
            {onCall && <CallOverlay
              onClose={() => setOnCall(false)}
              onTranscript={(callLines, seconds) => {
                const mm = Math.floor(seconds / 60), ss = String(seconds % 60).padStart(2, "0");
                setTurns((prev) => [
                  ...prev,
                  { role: "assistant" as const, content: `📞 Live call · ${mm}:${ss}` },
                  ...callLines.map((l) => ({
                    role: l.role === "you" ? ("user" as const) : ("assistant" as const),
                    content: l.text.trim(),
                  })),
                ]);
              }}
            />}
          <button className="btn btn-primary" onClick={() => send()} disabled={busy}>Send</button>
        </div>
      )}
    </div>
  );
}

function FilterRows({ rows }: { rows: FilterRow[] }) {
  return (
    <div className="omc-rows">
      {rows.map((r) => (
        <div key={r.id} className="omc-rowitem">
          <span>{emojiForScore(r.score)} {fmtDay(r.logged_on)}{r.note ? <span className="muted"> · {r.note}</span> : null}</span>
          {r.tags.length > 0 && <span className="muted" style={{ fontSize: 10 }}>{r.tags.join(" · ")}</span>}
        </div>
      ))}
    </div>
  );
}
